/**
 * Многострочное поле (§2.3 «Textarea», §5.2).
 *
 * Поведение в таблице: Enter фиксирует правку, Shift+Enter делает перенос
 * строки (§5.2). Поэтому фиксация — это `onCommit`, а не `onBlur`: уход
 * фокусом и нажатие Enter в черновике означают одно и то же, но Enter должен
 * ещё и перевести курсор на строку ниже — это делает владелец поля.
 *
 * Разметка та же, что у <Input> (.field*): одна анатомия поля на оба контрола.
 */
import { forwardRef, useId } from 'react'
import type { KeyboardEvent, ReactNode, TextareaHTMLAttributes } from 'react'
import './Input.css'
import './Textarea.css'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
  error?: string
  hint?: ReactNode
  /** Enter фиксирует значение, Shift+Enter переносит строку (§5.2). */
  commitOnEnter?: boolean
  onCommit?: () => void
  /** Поле развёрнуто поверх сетки (Alt+Enter в ячейке). */
  overlay?: boolean
  className?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    invalid = false,
    error,
    hint,
    commitOnEnter = false,
    onCommit,
    overlay = false,
    disabled = false,
    rows = 3,
    className,
    id,
    onKeyDown,
    'aria-describedby': describedBy,
    ...rest
  },
  ref,
) {
  const reactId = useId()
  const fieldId = id ?? `textarea${reactId}`
  const errorId = `${fieldId}-error`
  const hintId = `${fieldId}-hint`
  const isInvalid = invalid || Boolean(error)

  const classes = ['field', 'field--textarea']
  if (isInvalid) classes.push('field--invalid')
  if (disabled) classes.push('field--disabled')
  if (overlay) classes.push('field--overlay')
  if (className) classes.push(className)

  const described = [describedBy, error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ')

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (commitOnEnter && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      onCommit?.()
    }
    onKeyDown?.(event)
  }

  return (
    <div className={classes.join(' ')}>
      <div className="field__control">
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows}
          className="field__textarea t-cell"
          disabled={disabled}
          aria-invalid={isInvalid || undefined}
          aria-describedby={described || undefined}
          onKeyDown={handleKeyDown}
          {...rest}
        />
      </div>
      {error ? (
        <span id={errorId} className="field__error t-meta">
          {error}
        </span>
      ) : null}
      {hint ? (
        <span id={hintId} className="field__hint t-meta">
          {hint}
        </span>
      ) : null}
    </div>
  )
})
